import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Truck, Plus, Edit, Trash2, Globe, Phone } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', scac: '', phone: '', website: '', default_freight_terms: '', notes: '' };

const CarriersPage = () => {
  const { api } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api().get('/carriers'); setCarriers(r.data || []); }
    catch { toast.error('Failed to load carriers'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(emptyForm); setEditingId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name || '', scac: c.scac || '', phone: c.phone || '',
      website: c.website || '', default_freight_terms: c.default_freight_terms || '',
      notes: c.notes || '',
    });
    setEditingId(c.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Carrier name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        scac: form.scac || null,
        phone: form.phone || null,
        website: form.website || null,
        default_freight_terms: form.default_freight_terms || null,
        notes: form.notes || null,
      };
      if (editingId) {
        await api().put(`/carriers/${editingId}`, payload);
        toast.success(`${form.name} updated`);
      } else {
        await api().post('/carriers', payload);
        toast.success(`${form.name} saved`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete carrier "${c.name}"? Any BoL/PS/RO that references it must be renamed first.`)) return;
    try {
      await api().delete(`/carriers/${c.id}`);
      toast.success(`${c.name} deleted`); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="carriers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Truck className="w-7 h-7 text-slate-700" /> Carriers Address Book</h1>
          <p className="text-sm text-slate-500 mt-1">Frequently-used freight carriers. Saved SCAC + freight terms auto-fill on BoL and Packing Slip forms.</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="carrier-new-btn"><Plus className="w-4 h-4 mr-1" /> New Carrier</Button>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg">All Carriers ({carriers.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-slate-500">Loading…</div>
          : carriers.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No carriers saved yet</p>
              <p className="text-xs mt-1">Add your common freight carriers so BoL / PS forms pre-fill SCAC and freight terms.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>SCAC</TableHead>
                <TableHead>Phone</TableHead><TableHead>Website</TableHead>
                <TableHead>Default Terms</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {carriers.map(c => (
                  <TableRow key={c.id} data-testid={`carrier-row-${c.name}`}>
                    <TableCell className="font-semibold">{c.name}</TableCell>
                    <TableCell className="mono text-sm">{c.scac || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm">{c.phone ? <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" />{c.phone}</a> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm">{c.website ? <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:underline text-blue-600"><Globe className="w-3 h-3" />Open</a> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm">{c.default_freight_terms || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`carrier-edit-${c.name}`}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`carrier-delete-${c.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="carrier-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Carrier' : 'New Carrier'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Old Dominion Freight Line" className="rounded-sm" data-testid="carrier-form-name" /></div>
            <div><Label>SCAC Code</Label><Input value={form.scac} onChange={(e) => setForm({ ...form, scac: e.target.value.toUpperCase() })} placeholder="4-letter" maxLength={4} className="rounded-sm mono" data-testid="carrier-form-scac" /></div>
            <div><Label>Default Freight Terms</Label><Input value={form.default_freight_terms} onChange={(e) => setForm({ ...form, default_freight_terms: e.target.value })} placeholder="e.g. Prepaid, Collect" className="rounded-sm" data-testid="carrier-form-terms" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" data-testid="carrier-form-phone" /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="e.g. odfl.com" className="rounded-sm" data-testid="carrier-form-website" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" data-testid="carrier-form-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="carrier-form-save-btn">
              {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Carrier')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarriersPage;
